import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import Joyride, { CallBackProps, STATUS, Step, EVENTS, ACTIONS } from 'react-joyride'
import { useLocation } from 'react-router-dom'
import { useTutorialSpotlight } from '../../hooks/useTutorialSpotlight.ts'
import { useSelector, useDispatch } from 'react-redux'
import {
  setTutorialCompleted,
  skipTutorial,
  closeTutorialModal,
  setTutorialStep,
} from '../../store/actions/dashboardActions'
import CustomTooltip from './CustomTooltip.tsx'
import styles from './TutorialSpotlight.module.scss'
import { registerTutorialRef } from '../../utils/tutorialRefsRegistry.ts'
import "./TutorialCss.css"
import useNavVisibility from '../../hooks/useNavVisibility.ts'

const DEFAULT_TUTORIAL_STATE = {
  isActive: false,
  currentStep: 0,
  completed: false,
  skipped: false,
  lastShownDate: null,
}

const TutorialSpotlight: React.FC = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const { ocultarEnMobile } = useNavVisibility()
  const tutorialState = useSelector((state: any) => state.dashboard?.tutorial ?? DEFAULT_TUTORIAL_STATE)
  const { steps: tutorialSteps, spotlightRect } = useTutorialSpotlight()
  const [stepIndex, setStepIndex] = useState(tutorialState.currentStep ?? 0)
  const [showFallbackOverlay, setShowFallbackOverlay] = useState(false)
  const [fallbackRect, setFallbackRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null)
  const bodyOverflowRef = useRef<string | null>(null)
  const bodyPaddingRef = useRef<string | null>(null)
  const tutorialIsActive = tutorialState.isActive
  const tutorialCurrentStep = tutorialState.currentStep

  const syncStep = useCallback(
    (index: number) => {
      setStepIndex(index)
      dispatch(setTutorialStep(index))
    },
    [dispatch]
  )

  const joyrideSteps: Step[] = useMemo(() => {
    return tutorialSteps.map((step) => {
      if (step.isIntro) {
        const introTarget = step.targetSelector || '[data-tutorial="tutorial-intro-fallback"]'
        return {
          target: introTarget,
          title: step.title,
          content: step.description,
          placement: step.position || 'center',
          disableBeacon: true,
          disableOverlayClose: true,
          hideCloseButton: true,
          spotlightClicks: false,
        } as Step
      }

      if (!step.targetSelector) {
        return {
          target: 'body',
          title: step.title,
          content: step.description,
          placement: step.position || 'center',
          disableBeacon: true,
          disableOverlayClose: true,
          hideCloseButton: true,
          spotlightClicks: false,
        } as Step
      }

      const elementId = step.targetSelector?.replace('[data-tutorial="', '').replace('"]', '') || ''
      return {
        target: `[data-tutorial="${elementId}"]`,
        title: undefined,
        content: step.description,
        placement: step.position || 'bottom',
        disableBeacon: true,
        disableOverlayClose: true,
        hideCloseButton: true,
        spotlightClicks: false,
      }
    })
  }, [tutorialSteps])

  useEffect(() => {
    if (!tutorialIsActive) {
      setStepIndex(0)
      document.body.classList.remove('tutorial-active')
      document.body.style.overflow = bodyOverflowRef.current || ''
      document.body.style.paddingRight = bodyPaddingRef.current || ''
      return
    }

    document.body.classList.add('tutorial-active')
    bodyOverflowRef.current = document.body.style.overflow
    bodyPaddingRef.current = document.body.style.paddingRight
    document.body.style.overflow = 'hidden'
    document.body.style.paddingRight = ''

    return () => {
      document.body.classList.remove('tutorial-active')
      document.body.style.overflow = bodyOverflowRef.current || ''
      document.body.style.paddingRight = bodyPaddingRef.current || ''
    }
  }, [tutorialIsActive])

  useEffect(() => {
    if (tutorialIsActive) {
      setStepIndex(tutorialCurrentStep ?? 0)
    }
  }, [tutorialCurrentStep, tutorialIsActive])

  useEffect(() => {
    if (spotlightRect) {
      setFallbackRect({
        top: spotlightRect.top,
        left: spotlightRect.left,
        width: spotlightRect.width,
        height: spotlightRect.height,
      })
    } else {
      setFallbackRect(null)
    }
  }, [spotlightRect])

  useEffect(() => {
    if (tutorialIsActive && fallbackRect) {
      setShowFallbackOverlay(true)
    } else {
      setShowFallbackOverlay(false)
    }
  }, [tutorialIsActive, fallbackRect])

  const waitForElement = useCallback((selector: string, maxAttempts = 20, delay = 50): Promise<HTMLElement | null> => {
    return new Promise((resolve) => {
      let attempts = 0
      const checkElement = () => {
        const element = document.querySelector(selector) as HTMLElement
        if (element) {
          resolve(element)
        } else if (attempts < maxAttempts) {
          attempts++
          setTimeout(checkElement, delay)
        } else {
          resolve(null)
        }
      }
      checkElement()
    })
  }, [])

  const ensureOverlay = useCallback(() => {
    const overlayNode = document.querySelector('.react-joyride__overlay')
    return !!overlayNode
  }, [])

  const advanceToStep = useCallback(async (currentIndex: number, targetIndex: number) => {
    if (targetIndex < 0 || targetIndex >= tutorialSteps.length) {
      return
    }

    const currentStep = tutorialSteps[currentIndex]
    const targetStep = tutorialSteps[targetIndex]
    if (!targetStep) {
      return
    }

    const requiresTabChange = targetStep.requiredTab && targetStep.requiredTab !== currentStep?.requiredTab
    const elementId = targetStep.targetSelector?.replace('[data-tutorial="', '').replace('"]', '')
    if (!elementId) {
      syncStep(targetIndex)
      ensureOverlay()
      return
    }
    const selector = `[data-tutorial="${elementId}"]`

    if (requiresTabChange) {
      const event = new CustomEvent('tutorial-change-tab', {
        detail: { tab: targetStep.requiredTab }
      })
      window.dispatchEvent(event)
    }

    const element = await waitForElement(selector)
    if (!element) {
      return
    }

    const rect = element.getBoundingClientRect()
    setFallbackRect({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    })
    syncStep(targetIndex)
    ensureOverlay()
    setTimeout(() => {
      ensureOverlay()
    }, 0)
    setTimeout(() => {
      ensureOverlay()
    }, 150)
  }, [tutorialSteps, waitForElement, syncStep, ensureOverlay])

  const handleJoyrideCallback = useCallback(async (data: CallBackProps) => {
    const { status, type, index, action } = data
    ensureOverlay()

    if (action === ACTIONS.CLOSE) {
      const event = new CustomEvent('tutorial-change-tab', {
        detail: { tab: 'obras' }
      })
      window.dispatchEvent(event)
      dispatch(skipTutorial())
      dispatch(closeTutorialModal())
      syncStep(0)
      return
    }

    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      const event = new CustomEvent('tutorial-change-tab', {
        detail: { tab: 'obras' }
      })
      window.dispatchEvent(event)
      if (status === STATUS.FINISHED) {
        dispatch(setTutorialCompleted())
      } else {
        dispatch(skipTutorial())
      }
      dispatch(closeTutorialModal())
      syncStep(0)
      return
    }

    if (type === EVENTS.TOUR_END) {
      const event = new CustomEvent('tutorial-change-tab', {
        detail: { tab: 'obras' }
      })
      window.dispatchEvent(event)
      dispatch(setTutorialCompleted())
      dispatch(closeTutorialModal())
      syncStep(0)
      return
    }

    if (type === EVENTS.TARGET_NOT_FOUND) {
      const direction = action === ACTIONS.PREV ? -1 : 1
      const fallbackIndex = index + direction
      await advanceToStep(index, fallbackIndex)
      return
    }

    if (type === EVENTS.STEP_AFTER) {
      if (index === tutorialSteps.length - 1 && action === ACTIONS.NEXT) {
        const event = new CustomEvent('tutorial-change-tab', {
          detail: { tab: 'obras' }
        })
        window.dispatchEvent(event)
        dispatch(setTutorialCompleted())
        dispatch(closeTutorialModal())
        syncStep(0)
        return
      }

      const direction = action === ACTIONS.PREV ? -1 : 1
      const newIndex = index + direction
      await advanceToStep(index, newIndex)
      return
    }
    if (type === EVENTS.STEP_BEFORE || type === EVENTS.TOOLTIP) {
      ensureOverlay()
    }
    if (type === EVENTS.STEP_BEFORE || type === EVENTS.TOOLTIP) {
      ensureOverlay()
    }
  }, [dispatch, tutorialSteps, advanceToStep, syncStep, ensureOverlay])

  useEffect(() => {
    if (!tutorialIsActive) {
      return
    }
    const raf = requestAnimationFrame(() => {
      ensureOverlay()
    })
    const timer = setTimeout(() => {
      ensureOverlay()
    }, 150)
    return () => {
      cancelAnimationFrame(raf)
      clearTimeout(timer)
    }
  }, [stepIndex, tutorialIsActive, ensureOverlay])

  const isDashboardRoute = location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/')

  if (!isDashboardRoute) {
    return null
  }

  if (!tutorialState.isActive || joyrideSteps.length === 0 || ocultarEnMobile) {
    return null
  }

  return (
    <>
      <Joyride
        steps={joyrideSteps}
        run={tutorialState.isActive}
        stepIndex={stepIndex}
        continuous={true}
        showProgress={true}
        showSkipButton={true}
        disableScrolling={true}
        callback={handleJoyrideCallback}
        tooltipComponent={CustomTooltip}
        styles={{
          options: {
            arrowColor: '#fff',
            backgroundColor: '#fff',
            overlayColor: 'rgba(0, 0, 0, 0)',
            primaryColor: '#234ba2',
            spotlightShadow: '0 0 15px rgba(0, 0, 0, 0.5)',
            textColor: '#333',
            zIndex: 10000,
          },
          overlay: {
            backgroundColor: 'transparent',
          },
        }}
        floaterProps={{
          disableAnimation: false,
          placement: 'auto',
        }}
        locale={{
          back: 'Atrás',
          close: 'Cerrar',
          last: 'Finalizar',
          next: 'Siguiente',
          open: 'Abrir',
          skip: 'Saltar tutorial',
        }}
      />
      {tutorialIsActive && showFallbackOverlay && fallbackRect && (
        <div className={styles.fallbackOverlay}>
          <div
            className={styles.fallbackHole}
            style={{
              top: `${fallbackRect.top}px`,
              left: `${fallbackRect.left}px`,
              width: `${fallbackRect.width}px`,
              height: `${fallbackRect.height}px`,
            }}
          />
        </div>
      )}
      <div
        className={styles.introTarget}
        data-tutorial="tutorial-intro-fallback"
        ref={(el) => registerTutorialRef('tutorial-intro-fallback', el)}
      />
    </>
  )
}

export default TutorialSpotlight
